import { useMemo } from "react";
import { useTran } from "@/hooks";
import { SearchableSelect } from "@/components/common";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const buildFlatOptions = (tree = []) => {
  const options = [];
  const visit = (nodes, depth) => {
    (nodes || []).forEach((node) => {
      const value = node.id || node._id || node.key;
      const label = node.name || node.title || node.companyName || value;
      if (!value) return;
      const isParentCompany = node.unitLevel === "parent_company";
      if (!isParentCompany) {
        options.push({ value, label, depth });
      }
      if (Array.isArray(node.children) && node.children.length) {
        visit(node.children, isParentCompany ? depth : depth + 1);
      }
    });
  };
  visit(tree, 0);
  return options;
};

const OrganizationSelect = ({
  value,
  onValueChange,
  disabled = false,
  fallbackLabel,
  placeholder,
  triggerClassName,
  searchableThreshold = 8,
  options = [],
  tree = [],
}) => {
  const { t } = useTran();
  const flatOptions = useMemo(() => {
    if (options.length > 0) return options;
    return buildFlatOptions(tree);
  }, [options, tree]);

  const fallback = useMemo(() => {
    if (!value) return null;
    const exists = flatOptions.some((option) => option.value === value);
    if (exists) return null;
    return { value, label: fallbackLabel || value, depth: 0 };
  }, [fallbackLabel, flatOptions, value]);

  const displayOptions = useMemo(() => {
    if (!fallback) return flatOptions;
    return [...flatOptions, fallback];
  }, [fallback, flatOptions]);

  const handleChange = (nextValue) => {
    const selected = displayOptions.find(
      (option) => option.value === nextValue,
    );
    onValueChange?.(nextValue, selected?.label);
  };

  if (displayOptions.length > searchableThreshold) {
    const searchOptions = displayOptions.map((option) => ({
      value: option.value,
      label:
        option.depth && option.depth > 0
          ? `${"--".repeat(option.depth)} ${option.label}`
          : option.label,
    }));

    return (
      <SearchableSelect
        value={value || ""}
        onChange={handleChange}
        options={searchOptions}
        placeholder={placeholder || t("Select an organization")}
        emptyText={t("No organizations available")}
        disabled={disabled}
        className={cn("w-full", triggerClassName)}
      />
    );
  }

  return (
    <Select
      value={value || undefined}
      onValueChange={handleChange}
      disabled={disabled}
    >
      <SelectTrigger className={cn("w-full", triggerClassName)}>
        <SelectValue placeholder={placeholder || t("Select an organization")} />
      </SelectTrigger>
      <SelectContent>
        {displayOptions.map((option) => (
          <SelectItem
            key={`org-${option.value}`}
            value={option.value}
            style={{ paddingLeft: option.depth * 12 + 12 }}
          >
            {option.label}
          </SelectItem>
        ))}
        {displayOptions.length === 0 && (
          <SelectItem value="__empty" disabled>
            {t("No organizations available")}
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
};

export default OrganizationSelect;
